import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import NowServingDisplay from '../components/NowServingDisplay';
import WhatsAppButton from '../components/WhatsAppButton';
import {
  Ticket, UserPlus, UserCheck, Search, X, History,
  ChevronDown, Loader, Clock, CalendarDays, CheckCircle2,
  Plus, Trash2,
} from 'lucide-react';
import { todayISO } from '../utils/clinicDate';

// No default slots — the clinic defines all their own slots via the custom
// slot builder. The grid starts empty and fills as slots are added.

function fmt24to12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour   = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

const emptyForm = {
  name: '', age: '', gender: 'Male', phone: '', address: '',
  reasonForVisit: '', medicalHistory: '',
};

export default function NewTokenPage() {
  const { showToast } = useToast();

  // Patient type
  const [patientType, setPatientType]     = useState('new');

  const [searchQuery, setSearchQuery]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching]         = useState(false);
  const [showDropdown, setShowDropdown]   = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Form — billing amount is now a single field combining bill + payment
  const [form, setForm] = useState(emptyForm);

  // Initial billing / payment at booking
  // billingAmt     — the amount billed (always set as billing.amount)
  // paidNow        — checkbox: patient paid something right now at booking
  // paidNowAmount  — how much they actually paid now (can be a partial amount)
  // payMethod      — payment method if paidNow
  const [billingAmt, setBillingAmt]   = useState('');
  const [paidNow, setPaidNow]         = useState(false);
  const [paidNowAmount, setPaidNowAmount] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [payMethod, setPayMethod]     = useState('cash');
  // eslint-disable-next-line no-unused-vars
  const [phoneSuggestion, setPhoneSuggestion] = useState(null);

  // Slot booking
  const [useSlot, setUseSlot]           = useState(false);
  const [slotDate, setSlotDate]         = useState(todayISO());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookedTimes, setBookedTimes]   = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [timeFormat, setTimeFormat]     = useState('12h'); // '12h' or '24h'

  // Custom slots only — no default grid
  const [customSlots, setCustomSlots]       = useState([]);
  const [customTime, setCustomTime]         = useState('');
  const [customDuration, setCustomDuration] = useState('30');

  const [saving, setSaving]       = useState(false);
  const [lastToken, setLastToken] = useState(null);

  // Active doctors — for multi-doctor slot assignment
  const [activeDoctors, setActiveDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState('');

  useEffect(() => {
    api.get('/auth/staff').then(r => {
      const docs = (r.data.staff || []).filter(s => s.role === 'doctor' && s.isActive);
      setActiveDoctors(docs);
      // Default to the current user if they are a doctor
      const me = docs.find(d => d.username === JSON.parse(atob((sessionStorage.getItem('dq_token')||'.').split('.')[1]||'e30='))?.username);
      if (me) setSelectedDoctorId(me._id);
      else if (docs.length > 0) setSelectedDoctorId(docs[0]._id);
    }).catch(() => {});
  }, []);

  // Phone-based auto-fill: when a phone number with 10+ digits is typed for
  // a NEW patient, check if an existing patient has that number. If found,
  // show a banner so the receptionist can use the Returning Patient flow instead.
  const phoneDebRef = useRef(null);

  const searchRef   = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // ── Load booked slots when date changes ──────────────────────────────────
  const loadSlots = useCallback(async (date) => {
    if (!date) return;
    setLoadingSlots(true);
    try {
      const res = await api.get('/patients/slots', { params: { date } });
      setBookedTimes(res.data.booked || []);
    } catch { setBookedTimes([]); }
    finally { setLoadingSlots(false); }
  }, []);

  useEffect(() => { if (useSlot) loadSlots(slotDate); }, [useSlot, slotDate, loadSlots]);

  // ── Phone lookup (new patient mode) ─────────────────────────────────────
  useEffect(() => {
    if (patientType !== 'new' || !form.phone) { setPhoneSuggestion(null); return; }
    const digits = form.phone.replace(/\D/g, '');
    if (digits.length < 10) { setPhoneSuggestion(null); return; }
    clearTimeout(phoneDebRef.current);
    phoneDebRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/patients/search', { params: { q: digits } });
        const match = (res.data.patients || []).find(p => p.phone && p.phone.replace(/\D/g, '') === digits);
        setPhoneSuggestion(match || null);
      } catch { setPhoneSuggestion(null); }
    }, 500);
    return () => clearTimeout(phoneDebRef.current);
  }, [form.phone, patientType]);

  // ── Patient search ───────────────────────────────────────────────────────
  const runSearch = useCallback(async (q) => {
    const t = q.trim();
    if (t.length < 2) { setSearchResults([]); setShowDropdown(false); return; }
    setSearching(true);
    try {
      const res = await api.get('/patients/search', { params: { q: t } });
      setSearchResults(res.data.patients || []);
      setShowDropdown(true);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(searchQuery), 280);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery, runSearch]);

  useEffect(() => {
    function h(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          searchRef.current && !searchRef.current.contains(e.target))
        setShowDropdown(false);
    }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────
  function selectPatient(p) {
    setSelectedPatient(p);
    setShowDropdown(false);
    setSearchQuery('');
    setForm({
      name: p.name || '', age: p.age ?? '', gender: p.gender || 'Male',
      phone: p.phone || '', address: p.address || '',
      medicalHistory: p.medicalHistory || '',
      reasonForVisit: '',
    });

  }

  function clearSelectedPatient() {
    setSelectedPatient(null); setForm(emptyForm); setSearchQuery('');
  }

  function switchType(type) {
    setPatientType(type); setSelectedPatient(null); setForm(emptyForm);
    setSearchQuery(''); setSearchResults([]); setShowDropdown(false);
    setLastToken(null); setBillingAmt(''); setPaidNow(false);
  }

  // ── Custom slot builder ──────────────────────────────────────────────────
  function addCustomSlot() {
    if (!customTime) { showToast('Enter a start time first.', 'error'); return; }
    const [h, m] = customTime.split(':').map(Number);
    const dur    = Number(customDuration) || 30;
    const endMin = h * 60 + m + dur;
    const endH   = Math.floor(endMin / 60) % 24;
    const endM   = endMin % 60;
    const end24  = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
    // Build the slot label in whichever format the user chose
    const label  = timeFormat === '12h'
      ? `${fmt24to12(customTime)}–${fmt24to12(end24)}`
      : `${customTime}–${end24}`;
    // Prevent duplicate times
    if (customSlots.some((s) => s.time === customTime)) {
      showToast(`A slot at ${customTime} already exists.`, 'error'); return;
    }
    setCustomSlots((prev) => [...prev, { time: customTime, label, custom: true }]);
    setCustomTime('');
  }

  function removeCustomSlot(time) {
    if (selectedSlot?.time === time) setSelectedSlot(null);
    setCustomSlots((prev) => prev.filter((s) => s.time !== time));
  }

  // All slots = only custom slots, sorted by time
  const allSlots = [...customSlots].sort((a, b) => a.time.localeCompare(b.time));

  // ── Submit ───────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();

    if ((patientType === 'returning') && !selectedPatient) {
      showToast('Please search for and select the patient first.', 'error'); return;
    }
    if (!form.name.trim()) {
      showToast('Patient name is required.', 'error'); return;
    }
    if (useSlot && !selectedSlot) {
      showToast('Please select a time slot or uncheck "Book a time slot."', 'error'); return;
    }

  const billNum = Number(billingAmt) || 0;
  // How much the patient is actually paying right now — can be less than
  // the full bill amount (a partial payment), capped so it never exceeds it.
  const paidNowNum = Math.min(Number(paidNowAmount) || 0, billNum);

    setSaving(true);
    try {
      const payload = {
        ...form,
        age:           form.age ? Number(form.age) : undefined,
        billingAmount: billNum,
        ...(patientType === 'returning' && selectedPatient
          ? { sourcePatientId: selectedPatient._id, sourcePatientUID: selectedPatient.patientUID } : {}),
        ...(useSlot && selectedSlot ? {
          appointmentDate: slotDate,
          appointmentTime: selectedSlot.time,
          appointmentSlot: selectedSlot.label,
        } : {}),
        // Doctor assignment — only sent when multiple doctors exist
        ...(selectedDoctorId ? {
          doctorId:   selectedDoctorId,
          doctorName: activeDoctors.find(d => d._id === selectedDoctorId)?.displayName
                   || activeDoctors.find(d => d._id === selectedDoctorId)?.username
                   || '',
        } : {}),
        // If "paid at booking" is checked, whatever amount was actually paid
        // now (which may be a partial amount, not necessarily the full bill)
        // is sent as initialPayment, so the backend records it as paid and
        // creates a proper "Record a Payment" history entry for this patient.
        ...(paidNow && paidNowNum > 0 ? {
          initialPayment:       paidNowNum,
          initialPaymentMethod: payMethod,
        } : {}),
      };

      const res  = await api.post('/patients', payload);
      const p    = res.data.patient;
      const slot = p.appointmentSlot
        ? ` · ${p.appointmentSlot} on ${p.appointmentDate}` : '';
      const paid = paidNow && paidNowNum > 0
        ? ` · ₹${paidNowNum.toLocaleString('en-IN')} paid` : '';
      showToast(`Token #${p.tokenNumber} created for ${p.name}${slot}${paid}`, 'success');

      setLastToken(p);
      setForm(emptyForm);
      setSelectedPatient(null);
      setSearchQuery('');
      setSelectedSlot(null);
      setBillingAmt('');
      setPaidNow(false);
      setPaidNowAmount('');
      if (useSlot) loadSlots(slotDate);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not create token.', 'error');
    } finally { setSaving(false); }
  }

  // eslint-disable-next-line no-unused-vars
  const update   = (f, v) => setForm((s) => ({ ...s, [f]: v }));
  const dimmed   = patientType === 'returning' && !selectedPatient;
  // eslint-disable-next-line no-unused-vars
  const dimStyle = (extra = {}) => ({
    opacity:       dimmed ? 0.4 : 1,
    pointerEvents: dimmed ? 'none' : 'auto',
    transition:    'opacity 0.2s',
    ...extra,
  });

  // eslint-disable-next-line no-unused-vars

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AppLayout title="Create Token" subtitle="Issue a queue token for a new or returning patient">
      <div style={{ marginBottom: 20 }}><NowServingDisplay /></div>

      <div className="token-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.6fr) minmax(280px,1fr)', gap: 24, alignItems: 'start' }}>
        <form onSubmit={handleSubmit} className="card" style={{ padding: 24 }}>

          {/* ── Patient type toggle ──────────────────────────────────── */}
          <div style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-ink-soft)', marginBottom: 10 }}>Patient Type</div>
            <div style={{ display: 'flex', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden', width: 'fit-content' }}>
              <TypeTab active={patientType === 'new'}        onClick={() => switchType('new')}        icon={UserPlus}      label="New Patient" />
              <TypeTab active={patientType === 'returning'}  onClick={() => switchType('returning')}  icon={UserCheck}     label="Returning Patient" />
            </div>
          </div>

          {/* ── Returning / Reschedule patient search ───────────────── */}
          {(patientType === 'returning' || patientType === 'reschedule') && (
            <div style={{ marginBottom: 20, position: 'relative' }}>

              {!selectedPatient ? (
                <>
                  <label className="label" style={{ marginBottom: 8 }}>Search by name, Patient ID, or phone</label>
                  <div style={{ position: 'relative' }} ref={searchRef}>
                    <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)', pointerEvents: 'none' }} />
                    <input className="input" style={{ paddingLeft: 36, paddingRight: 36 }}
                      value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="e.g. Priya, PT-000002, 9876543211" autoFocus autoComplete="off" />
                    {searching && <Loader size={14} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)', animation: 'spin 1s linear infinite' }} />}
                  </div>
                  {showDropdown && (
                    <div ref={dropdownRef} className="card"
                      style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200, padding: 0, overflow: 'hidden', boxShadow: 'var(--shadow-lg)', maxHeight: 300, overflowY: 'auto' }}>
                      {searchResults.length === 0
                        ? <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--color-ink-faint)' }}>No patients found.</div>
                        : searchResults.map((p) => (
                          <button key={p._id} type="button" onClick={() => selectPatient(p)}
                            style={{ width: '100%', textAlign: 'left', padding: '12px 16px', border: 'none', background: 'transparent', cursor: 'pointer', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 12 }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-primary-soft)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                            <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--color-primary-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--color-primary)', flexShrink: 0 }}>
                              {p.name.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                              <div style={{ fontSize: 12, color: 'var(--color-ink-faint)', display: 'flex', gap: 10 }}>
                                <span style={{ fontFamily: 'var(--font-mono)' }}>{p.patientUID}</span>
                                {p.phone && <span>· {p.phone}</span>}
                                {p.age && <span>· {p.age}y</span>}
                              </div>
                              {p.tokenDate && (
                                <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', marginTop: 2 }}>
                                  <History size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />Last visit: {p.tokenDate}
                                </div>
                              )}
                            </div>
                            <ChevronDown size={14} style={{ color: 'var(--color-ink-faint)', transform: 'rotate(-90deg)' }} />
                          </button>
                        ))}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--color-primary-soft)', borderRadius: 10, border: '1px solid var(--color-primary)' }}>
                  <UserCheck size={18} color="var(--color-primary)" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--color-primary)' }}>{selectedPatient.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-primary)', opacity: 0.8 }}>
                      {selectedPatient.patientUID}{selectedPatient.phone && ` · ${selectedPatient.phone}`}
                    </div>
                  </div>
                  <button type="button" className="btn btn-icon btn-ghost" onClick={clearSelectedPatient}><X size={16} /></button>
                </div>
              )}
            </div>
          )}

          {patientType === 'returning' && selectedPatient && (
            <div style={{ fontSize: 12, color: 'var(--color-ink-soft)', padding: '8px 12px', borderRadius: 8, background: 'var(--color-surface-raised)', border: '1px solid var(--color-border)', marginBottom: 16, lineHeight: 1.5 }}>
              Pre-filled from <strong>{selectedPatient.name}'s</strong> previous record. Edit any field — changes apply only to today's visit.
            </div>
          )}

          {/* ── Patient details form ──────────────────────────────────── */}
          <div style={{ display:'grid', gap:14, marginBottom:4 }}>
            {/* Name — full width */}
            <div className="field" style={{ marginBottom:0 }}>
              <label className="label">Full Name *</label>
              <input className="input" value={form.name}
                onChange={e => setForm(s => ({...s, name: e.target.value}))}
                placeholder="e.g. Ramesh Kumar" autoComplete="name" />
            </div>

            {/* Age / Gender / Phone / Address — single row */}
            <div style={{ display:'grid', gridTemplateColumns:'72px 120px 1fr 1fr', gap:10, alignItems:'start' }}>
              <div className="field" style={{ marginBottom:0 }}>
                <label className="label">Age</label>
                <input className="input" type="number" min="0" max="130"
                  value={form.age} onChange={e => setForm(s => ({...s, age: e.target.value}))}
                  placeholder="35" />
              </div>
              <div className="field" style={{ marginBottom:0 }}>
                <label className="label">Gender</label>
                <select className="select" value={form.gender}
                  onChange={e => setForm(s => ({...s, gender: e.target.value}))}>
                  <option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
              <div className="field" style={{ marginBottom:0 }}>
                <label className="label">Phone</label>
                <input className="input" value={form.phone}
                  onChange={e => { setForm(s => ({...s, phone: e.target.value})); setPhoneSuggestion(null); }}
                  placeholder="9876543210" />
              </div>
              <div className="field" style={{ marginBottom:0 }}>
                <label className="label">Address</label>
                <input className="input" value={form.address}
                  onChange={e => setForm(s => ({...s, address: e.target.value}))}
                  placeholder="Locality, City" />
              </div>
            </div>

            {/* Phone suggestion banner */}
            {phoneSuggestion && patientType === 'new' && (
              <div style={{ padding:'10px 14px', borderRadius:8, background:'var(--color-warning-soft)', border:'1px solid var(--color-warning)', fontSize:13, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <div style={{ flex:1 }}>
                  <strong style={{ color:'var(--color-warning)' }}>Existing patient found:</strong>{' '}
                  {phoneSuggestion.name} · {phoneSuggestion.patientUID}
                  {phoneSuggestion.age && ` · ${phoneSuggestion.age}y`}
                  <div style={{ fontSize:12, color:'var(--color-ink-soft)', marginTop:2 }}>Last visit: {phoneSuggestion.tokenDate || 'unknown'}</div>
                </div>
                <button type="button" className="btn btn-sm"
                  style={{ background:'var(--color-warning)', color:'white', whiteSpace:'nowrap' }}
                  onClick={() => { switchType('returning'); setTimeout(()=>selectPatient(phoneSuggestion),50); }}>
                  Use as Returning Patient →
                </button>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setPhoneSuggestion(null)}>Ignore</button>
              </div>
            )}

            {/* Medical History */}
            <div className="field" style={{ marginBottom:0 }}>
              <label className="label">Medical History</label>
              <textarea className="input" rows={2}
                value={form.medicalHistory}
                onChange={e => setForm(s => ({...s, medicalHistory: e.target.value}))}
                placeholder="Allergies, existing conditions, medications…"
                style={{ resize:'vertical' }} />
            </div>

            {/* Reason for visit */}
            <div className="field" style={{ marginBottom:0 }}>
              <label className="label">Reason for Visit</label>
              <input className="input" value={form.reasonForVisit}
                onChange={e => setForm(s => ({...s, reasonForVisit: e.target.value}))}
                placeholder="e.g. Toothache, Cleaning, Routine checkup" />
            </div>
          </div>

                    {/* ── Time Slot Booking ────────────────────────────────────── */}
          {patientType !== 'reschedule' && (
          <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid var(--color-border)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
              <input type="checkbox" checked={useSlot}
                onChange={(e) => { setUseSlot(e.target.checked); setSelectedSlot(null); }}
                style={{ width: 17, height: 17, accentColor: 'var(--color-primary)' }} />
              <CalendarDays size={17} color="var(--color-primary)" />
              Book a specific time slot
              <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-ink-faint)' }}>(optional — leave unchecked for walk-in)</span>
            </label>

            {useSlot && (
              <div style={{ marginTop: 16 }}>
                {/* Appointment date */}
                <div className="field">
                  <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CalendarDays size={13} color="var(--color-primary)" />
                    Appointment Date
                    <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--color-ink-faint)' }}>
                      — the date this slot is booked for (today or any future date)
                    </span>
                  </label>
                  <input className="input" type="date" value={slotDate}
                    onChange={(e) => { setSlotDate(e.target.value); setSelectedSlot(null); }}
                    style={{ maxWidth: 220 }} />
                </div>

                {/* Custom slot builder */}
                <div style={{ marginBottom: 14, padding: '14px 16px', borderRadius: 8, border: '1px dashed var(--color-border)', background: 'var(--color-surface)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'var(--color-ink-soft)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Plus size={14} /> Add a time slot</span>
                    {/* 12h / 24h format toggle */}
                    <div style={{ display: 'flex', border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden', fontSize: 12 }}>
                      {['12h', '24h'].map(fmt => (
                        <button key={fmt} type="button"
                          onClick={() => setTimeFormat(fmt)}
                          style={{ padding: '4px 10px', border: 'none', cursor: 'pointer', fontWeight: 700, background: timeFormat === fmt ? 'var(--color-primary)' : 'transparent', color: timeFormat === fmt ? 'white' : 'var(--color-ink-soft)', transition: 'all 0.12s' }}>
                          {fmt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label className="label">Start time</label>
                      {timeFormat === '24h' ? (
                        /* 24h mode: plain HTML time input, no AM/PM */
                        <input
                          className="input"
                          type="time"
                          value={customTime}
                          onChange={e => setCustomTime(e.target.value)}
                          style={{ width: 130 }}
                        />
                      ) : (
                        /* 12h mode: hour + minute selects + AM/PM toggle */
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          {/* Hour select (1–12) */}
                          <select className="select" style={{ width: 68, padding: '8px 6px' }}
                            value={(() => {
                              if (!customTime) return '12';
                              const h = parseInt(customTime.split(':')[0], 10);
                              return String(h % 12 || 12);
                            })()}
                            onChange={e => {
                              const h12    = parseInt(e.target.value, 10);
                              const mins   = customTime ? customTime.split(':')[1] : '00';
                              const curH   = customTime ? parseInt(customTime.split(':')[0], 10) : 9;
                              const isPM   = curH >= 12;
                              let h24 = h12 % 12;
                              if (isPM) h24 += 12;
                              setCustomTime(`${String(h24).padStart(2,'0')}:${mins}`);
                            }}>
                            {[12,1,2,3,4,5,6,7,8,9,10,11].map(h =>
                              <option key={h} value={h}>{h}</option>
                            )}
                          </select>
                          <span style={{ fontWeight: 700, color: 'var(--color-ink-faint)' }}>:</span>
                          {/* Minute select */}
                          <select className="select" style={{ width: 68, padding: '8px 6px' }}
                            value={customTime ? customTime.split(':')[1] : '00'}
                            onChange={e => {
                              const h24 = customTime ? customTime.split(':')[0] : '09';
                              setCustomTime(`${h24}:${e.target.value}`);
                            }}>
                            {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m =>
                              <option key={m} value={m}>{m}</option>
                            )}
                          </select>
                          {/* AM/PM toggle — only visible in 12h mode */}
                          <div style={{ display: 'flex', border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden' }}>
                            {['AM','PM'].map(period => {
                              const curH     = customTime ? parseInt(customTime.split(':')[0], 10) : 9;
                              const isActive = period === 'AM' ? curH < 12 : curH >= 12;
                              return (
                                <button key={period} type="button"
                                  onClick={() => {
                                    if (!customTime) { setCustomTime(period === 'AM' ? '09:00' : '13:00'); return; }
                                    const [hStr, mStr] = customTime.split(':');
                                    let h = parseInt(hStr, 10);
                                    if (period === 'AM' && h >= 12) h -= 12;
                                    if (period === 'PM' && h <  12) h += 12;
                                    setCustomTime(`${String(h).padStart(2,'0')}:${mStr}`);
                                  }}
                                  style={{
                                    padding: '6px 10px', border: 'none', cursor: 'pointer',
                                    fontWeight: 700, fontSize: 12, transition: 'all 0.12s',
                                    background: isActive ? 'var(--color-primary)' : 'transparent',
                                    color:      isActive ? 'white'                 : 'var(--color-ink-soft)',
                                  }}>
                                  {period}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label className="label">Duration</label>
                      <select className="select" value={customDuration}
                        onChange={(e) => setCustomDuration(e.target.value)} style={{ width: 110 }}>
                        <option value="15">15 min</option>
                        <option value="20">20 min</option>
                        <option value="30">30 min</option>
                        <option value="45">45 min</option>
                        <option value="60">60 min</option>
                        <option value="90">90 min</option>
                      </select>
                    </div>
                    <button type="button" className="btn btn-outline btn-sm"
                      onClick={addCustomSlot}
                      style={{ alignSelf: 'flex-end', marginBottom: 0 }}>
                      <Plus size={13} /> Add slot
                    </button>
                  </div>
                </div>

                {/* Slot grid — custom slots only */}
                {allSlots.length === 0 ? (
                  <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--color-ink-faint)', fontSize: 13 }}>
                    No slots yet — add one above.
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-ink-soft)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Clock size={14} />
                      Slots for <strong>{slotDate}</strong>
                      {loadingSlots && <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))', gap: 7 }}>
                      {allSlots.map((slot) => {
                        // Find ALL bookings for this time slot (multiple doctors can share)
                        const slotBookings = bookedTimes.filter(b => b.appointmentTime === slot.time);
                        // This slot is only blocked for the SELECTED doctor
                        const bookedForMe  = slotBookings.find(b =>
                          selectedDoctorId ? String(b.doctorId) === String(selectedDoctorId) : !b.doctorId
                        );
                        const otherDoctors = slotBookings.filter(b =>
                          selectedDoctorId ? String(b.doctorId) !== String(selectedDoctorId) : b.doctorId
                        );
                        const isSelected   = selectedSlot?.time === slot.time;
                        const isBlocked    = !!bookedForMe;
                        return (
                          <div key={slot.time} style={{ position: 'relative' }}>
                            <button type="button"
                              disabled={isBlocked}
                              onClick={() => setSelectedSlot(isSelected ? null : slot)}
                              title={isBlocked
                                ? `Your slot is booked — ${bookedForMe.name} (Token #${bookedForMe.tokenNumber})`
                                : otherDoctors.length > 0
                                  ? `Available for you · ${otherDoctors.length} other doctor(s) also booked here`
                                  : slot.label}
                              style={{
                                width: '100%',
                                padding: '8px 4px', borderRadius: 8,
                                border: `2px solid ${isSelected ? 'var(--color-primary)' : otherDoctors.length > 0 ? 'var(--color-warning)' : 'var(--color-accent)'}`,
                                background: isSelected ? 'var(--color-primary)' : isBlocked ? 'var(--color-border)' : otherDoctors.length > 0 ? 'var(--color-warning-soft)' : 'var(--color-accent-soft)',
                                color: isSelected ? 'white' : isBlocked ? 'var(--color-ink-faint)' : otherDoctors.length > 0 ? 'var(--color-warning)' : 'var(--color-accent)',
                                fontSize: 12, fontWeight: 600,
                                cursor: isBlocked ? 'not-allowed' : 'pointer',
                                opacity: isBlocked ? 0.55 : 1,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                                transition: 'all 0.12s',
                              }}>
                              {isSelected && <CheckCircle2 size={12} />}
                              {slot.label}
                              {isBlocked && <span style={{ fontSize: 10 }}>Your slot taken</span>}
                              {!isBlocked && otherDoctors.length > 0 && (
                                <span style={{ fontSize: 10 }}>+{otherDoctors.length} Dr booked</span>
                              )}
                            </button>
                            {/* Remove custom slot button */}
                            {!isBlocked && slot.custom && (
                              <button type="button"
                                onClick={() => removeCustomSlot(slot.time)}
                                title="Remove this slot"
                                style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'var(--color-danger)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, zIndex: 1 }}>
                                <Trash2 size={10} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {selectedSlot && (
                  <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--color-primary-soft)', borderRadius: 8, fontSize: 13, color: 'var(--color-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircle2 size={15} />
                    Slot: <strong>{selectedSlot.label}</strong> on <strong>{slotDate}</strong>
                    {fmt24to12(selectedSlot.time) !== selectedSlot.time && (
                      <span style={{ fontWeight: 400, opacity: 0.8 }}>({fmt24to12(selectedSlot.time)})</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* ── Assign to Doctor ─────────────────────────────────────── */}
          {activeDoctors.length > 1 && (
            <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid var(--color-border)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-ink-soft)', marginBottom: 12 }}>
                Assign to Doctor
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {activeDoctors.map(doc => {
                  const name = doc.displayName || doc.username;
                  const qual = doc.specialization || '';
                  const room = doc.room || '';
                  const sel  = selectedDoctorId === doc._id;
                  return (
                    <button key={doc._id} type="button"
                      onClick={() => setSelectedDoctorId(doc._id)}
                      style={{
                        display:'flex', alignItems:'center', gap:10,
                        padding:'10px 16px', borderRadius:10, cursor:'pointer',
                        border: `2px solid ${sel ? 'var(--color-primary)' : 'var(--color-border)'}`,
                        background: sel ? 'var(--color-primary)' : 'var(--color-card)',
                        color: sel ? 'white' : 'var(--color-ink)',
                        minWidth: 150, transition: 'all 0.12s',
                      }}>
                      <div style={{ width:32, height:32, borderRadius:'50%', overflow:'hidden', flexShrink:0,
                        background: sel ? 'rgba(255,255,255,0.25)' : 'var(--color-primary-soft)',
                        display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {doc.profilePicture
                          ? <img src={doc.profilePicture} alt={name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                          : <span style={{ fontSize:13, fontWeight:800, color: sel ? 'white' : 'var(--color-primary)' }}>{name.charAt(0).toUpperCase()}</span>}
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start' }}>
                        <span style={{ fontWeight:700, fontSize:14 }}>Dr. {name}</span>
                        {qual && <span style={{ fontSize:11, opacity:0.75 }}>{qual}</span>}
                        {room && <span style={{ fontSize:11, opacity:0.9, fontWeight:600 }}>{room}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Initial Payment + Payment at Booking ──────────── */}
          <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-ink-soft)', marginBottom: 12 }}>
              Initial Payment (optional)
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="field" style={{ marginBottom: 0, flex: '1 1 140px' }}>
                <label className="label">Initial Payment (₹)</label>
                <input className="input" type="number" min="0" step="0.01"
                  value={billingAmt} onChange={e => setBillingAmt(e.target.value)}
                  placeholder="e.g. 500" />
              </div>
              {billingAmt && Number(billingAmt) > 0 && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingBottom: 2 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    <input type="checkbox" checked={paidNow} onChange={e => {
                      const checked = e.target.checked;
                      setPaidNow(checked);
                      // Default the paid-now amount to the full amount when first
                      // checked, but it stays freely editable for a partial payment.
                      if (checked && !paidNowAmount) setPaidNowAmount(billingAmt);
                    }}
                      style={{ width: 15, height: 15, accentColor: 'var(--color-primary)' }} />
                    Paid now
                  </label>
                </div>
              )}
            </div>
            {paidNow && billingAmt && Number(billingAmt) > 0 && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 10 }}>
                <div className="field" style={{ marginBottom: 0, flex: '1 1 140px' }}>
                  <label className="label">Amount Paid Now (₹)</label>
                  <input className="input" type="number" min="0" max={billingAmt} step="0.01"
                    value={paidNowAmount} onChange={e => setPaidNowAmount(e.target.value)}
                    placeholder="e.g. 200" />
                </div>
                <div className="field" style={{ marginBottom: 0, flex: '1 1 120px' }}>
                  <label className="label">Method</label>
                  <select className="select" value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="card">Card</option>
                    <option value="bank">Bank Transfer</option>
                  </select>
                </div>
              </div>
            )}
            {billingAmt && Number(billingAmt) > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-ink-faint)' }}>
                {paidNow && Number(paidNowAmount) > 0
                  ? (() => {
                      const pn = Math.min(Number(paidNowAmount) || 0, Number(billingAmt));
                      const rem = Number(billingAmt) - pn;
                      return (
                        <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>
                          ✓ ₹{pn.toLocaleString('en-IN')} will be recorded as paid via {payMethod.toUpperCase()}
                          {rem > 0 ? ` — ₹${rem.toLocaleString('en-IN')} remains pending` : ' — fully paid'}
                        </span>
                      );
                    })()
                  : <span>₹{Number(billingAmt).toLocaleString('en-IN')} will be recorded as the initial bill, pending — collect payment later in Billing</span>
                }
              </div>
            )}
          </div>

          <button type="submit" className="btn btn-primary"
            disabled={saving || (patientType === 'returning' && !selectedPatient)}
            style={{ marginTop: 22, width: '100%' }}>
            <Ticket size={16} />
            {saving ? 'Creating token…' : 'Create Token'}
          </button>
        </form>

        {/* ── Right panel ──────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {lastToken ? (
            <div className="ticket">
              <div className="ticket-stub">
                <div className="ticket-stub-label">Token</div>
                <div className="ticket-stub-number">{lastToken.tokenNumber}</div>
              </div>
              <div className="ticket-body">
                <div className="ticket-name">{lastToken.name}</div>
                <div className="ticket-meta">{lastToken.gender}{lastToken.age ? `, ${lastToken.age}y` : ''}</div>
                {lastToken.appointmentSlot && (
                  <div style={{ fontSize: 12, color: 'var(--color-primary)', fontWeight: 600, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={12} /> {lastToken.appointmentSlot} · {lastToken.appointmentDate}
                  </div>
                )}
                {lastToken.billing?.amountPaid > 0 && (
                  <div style={{ fontSize: 12, marginTop: 4, color: 'var(--color-success)', fontWeight: 600 }}>
                    ₹{lastToken.billing.amountPaid.toLocaleString('en-IN')} paid at booking
                  </div>
                )}
                <div className="ticket-uid">{lastToken.patientUID}</div>
                {lastToken.phone && (
                  <div style={{ marginTop: 10 }}>
                    <WhatsAppButton
                      patient={lastToken}
                      slot={lastToken.appointmentSlot}
                      date={lastToken.appointmentDate || lastToken.tokenDate}
                      tokenNumber={lastToken.tokenNumber}
                      size="sm"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--color-ink-faint)' }}>
              <Ticket size={32} style={{ marginBottom: 10, opacity: 0.5 }} />
              <p style={{ fontSize: 13, margin: 0 }}>Most recently issued token appears here.</p>
            </div>
          )}

          <div className="card" style={{ padding: 18, fontSize: 13, color: 'var(--color-ink-soft)', lineHeight: 1.6 }}>
            {patientType === 'new'
              ? <><strong>New Patient</strong> — a permanent Patient ID (e.g. PT-000010) will be created. Optionally record an initial payment and book a time slot.</>
              : <><strong>Returning Patient</strong> — search to retrieve their details. Their original Patient ID is reused across all visits.</>
            }
          </div>

          {patientType === 'returning' && selectedPatient?.medicalHistory && (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-ink-faint)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                <History size={13} /> Previous Medical History
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-ink-soft)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{selectedPatient.medicalHistory}</div>
            </div>
          )}
          {patientType === 'returning' && selectedPatient?.doctorNotes && (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-ink-faint)', marginBottom: 8 }}>Last Doctor's Notes</div>
              <div style={{ fontSize: 13, color: 'var(--color-ink-soft)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{selectedPatient.doctorNotes}</div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: translateY(-50%) rotate(0deg); }
          to   { transform: translateY(-50%) rotate(360deg); }
        }
      `}</style>
    </AppLayout>
  );
}

function TypeTab({ active, onClick, icon: Icon, label }) {
  return (
    <button type="button" onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', border: 'none', cursor: 'pointer', background: active ? 'var(--color-primary)' : 'transparent', color: active ? 'white' : 'var(--color-ink-soft)', fontWeight: 700, fontSize: 14, transition: 'background 0.15s, color 0.15s' }}>
      <Icon size={16} />{label}
    </button>
  );
}
